import React, { useState } from "react";
import { ArrowLeft, Plus, RotateCw } from "lucide-react";
import { finApi, finPref } from "@/lib/financeApi";
import { btnGold, Chip, Empty, ErrorBox, Loading, StatusFilter, SummaryChip, useLoad, when } from "@/components/nds/windows/finance/parts";
import { DocAttach, HistoryList, RefLedger } from "@/components/nds/windows/finance/recordParts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { WorkflowActions, WorkflowForm } from "@/components/nds/windows/finance/WorkflowParts";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";
import { BulkBar } from "@/components/nds/windows/finance/WorkflowBulk";

function Detail({ cfg, id, onBack }) {
  const [tab, setTab] = useState("details");
  const api = cfg.client || finApi;
  const [state, load] = useLoad(() => api.get(`${cfg.api}/${id}`), [id]);
  const back = <button type="button" onClick={onBack} data-testid={`${cfg.testid}-back`} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All {cfg.plural}</button>;
  if (state.loading && !state.data) return <div className="p-5">{back}<Loading testid={`${cfg.testid}-detail-loading`} /></div>;
  if (state.error) return <div className="p-5">{back}<ErrorBox testid={`${cfg.testid}-detail-error`} error={state.error} onRetry={load} /></div>;
  const rec = state.data;
  const [tone, l] = cfg.chips[rec.status];
  const Extra = cfg.Extra;
  return (
    <div data-testid={`${cfg.testid}-detail`} className="flex flex-col h-full">
      <div className="px-5 pt-4 space-y-3 border-b border-white/10">
        {back}
        <div className="flex items-center gap-3"><h3 className="text-lg text-nex-silver font-semibold">{cfg.title(rec)}</h3><Chip tone={tone} data-testid={`${cfg.testid}-detail-status`}>{l}</Chip></div>
        <div className="flex gap-4">
          {[["details", cfg.tabLabels?.details || "Details"], ...(cfg.extraTabs || []), ["history", cfg.tabLabels?.history || "History"], ...(cfg.noLedger ? [] : [["ledger", "Ledger"]])].map(([t, label]) => (
            <button key={t} type="button" onClick={() => setTab(t)} data-testid={`${cfg.testid}-tab-${t}`} aria-pressed={tab === t}
              className={`pb-2 font-mono text-[10px] tracking-[0.2em] uppercase border-b-2 transition-colors ${tab === t ? "border-nex-gold text-nex-gold" : "border-transparent text-nex-muted hover:text-nex-silver"}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {tab === "details" && (
          <div data-testid={`${cfg.testid}-details-tab`} className="p-5 space-y-4">
            {cfg.actionsLast && Extra && <Extra rec={rec} reload={load} cfg={cfg} />}
            <WorkflowActions cfg={cfg} rec={rec} onChanged={load} />
            {cfg.After && <cfg.After rec={rec} reload={load} />}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...cfg.fields(rec), ["Created", `${rec.created_by?.name || "—"} · ${when(rec.created_at)}`], ...(rec.void_reason ? [["Void reason", rec.void_reason]] : [])].map(([k, v]) => (
                <div key={k} className="space-y-0.5"><div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{k}</div><div className="text-sm text-nex-silver break-words">{v || "—"}</div></div>
              ))}
            </div>
            {!cfg.actionsLast && Extra && <Extra rec={rec} reload={load} cfg={cfg} />}
            {!cfg.noDocs && <DocAttach client={api} prefix={cfg.api} rec={{ ...rec, document_url: rec[cfg.docField || "document_url"] }} onChanged={load} testid={cfg.testid} locked={cfg.docLocked?.includes(rec.status)} />}
          </div>
        )}
        {(cfg.extraTabs || []).map(([k, , Comp]) => tab === k && <Comp key={k} rec={rec} reload={load} />)}
        {tab === "history" && <HistoryList history={rec.history} chips={cfg.chips} testid={`${cfg.testid}-history`} />}
        {tab === "ledger" && <RefLedger refType={cfg.refType} refId={rec.id} testid={`${cfg.testid}-ledger`} deps={[rec.status]} />}
      </div>
    </div>
  );
}

/** Shared card window for status-workflow finance collections. */
export default function WorkflowContent({ cfg, initial }) {
  const isSuper = useIsSuper();
  const pref = cfg.pref || finPref;
  const [status, setStatusState] = useState(() => pref.get(`${cfg.key}_status`, "all"));
  const [adding, setAdding] = useState(!!initial?.adding);
  const [selected, setSelected] = useState(initial?.open || null);
  const [picked, setPicked] = useState(initial?.resident || null);
  const [checked, setChecked] = useState([]);
  const setStatus = (v) => { pref.set(`${cfg.key}_status`, v); setStatusState(v); setChecked([]); };
  const api = cfg.client || finApi;
  const [state, load] = useLoad(() => Promise.all([api.get(cfg.api), api.get(`${cfg.api}/summary`)])
    .then(([l, s]) => ({ records: l.records, summary: s })), [cfg.api]);

  if (cfg.saOnly && !isSuper) return <Empty testid={`${cfg.testid}-forbidden`}>Only a Super Admin can view {cfg.plural}.</Empty>;
  if (selected) return <Detail cfg={cfg} id={selected} onBack={() => { setSelected(null); load(); }} />;
  if (state.loading && !state.data) return <Loading testid={`${cfg.testid}-loading`} label={`Loading ${cfg.plural}…`} />;
  if (state.error) return <ErrorBox testid={`${cfg.testid}-error`} error={state.error} onRetry={load} />;
  const { records, summary } = state.data;
  const mine = initial?.only ? records.filter((r) => r.resident_id === initial.only || r.resident_ids?.includes(initial.only)) : records;
  const counts = { all: mine.length };
  mine.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
  const list = status === "all" ? mine : mine.filter((r) => r.status === status);
  const bulk = cfg.actions?.some((a) => a.bulk) && isSuper;
  const pickable = list.filter((r) => cfg.actions.some((a) => a.bulk && a.from.includes(r.status)));
  const allOn = pickable.length > 0 && pickable.every((r) => checked.includes(r.id));
  const tick = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  return (
    <div data-testid={`${cfg.testid}-content`} className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <SummaryChip tone={typeof cfg.summaryTone === "function" ? cfg.summaryTone(summary) : cfg.summaryTone} testid={`${cfg.testid}-summary`}>{cfg.summaryText(summary)}</SummaryChip>
          {cfg.HeaderAction && <cfg.HeaderAction resident={adding ? picked : null} />}
          {!adding && ((!cfg.saOnly && !cfg.createSa) || isSuper) && <button type="button" onClick={() => setAdding(true)} data-testid={`${cfg.testid}-new`} className={`${btnGold} inline-flex items-center gap-1.5 ml-auto`}><Plus className="h-3 w-3" /> New {cfg.label.toLowerCase()}</button>}
          <button type="button" onClick={load} data-testid={`${cfg.testid}-refresh`} aria-label="Refresh" className={`p-1.5 text-nex-muted hover:text-nex-gold ${adding ? "ml-auto" : ""}`}><RotateCw className="h-3.5 w-3.5" /></button>
        </div>
        <StatusFilter prefix={cfg.testid} chips={cfg.chips} value={status} onChange={setStatus} counts={counts} />
      </div>
      {adding && <div className="px-5 pt-3">{React.createElement(cfg.CreateForm || WorkflowForm, { cfg, initial: initial?.resident, onPick: setPicked, onCancel: () => { setAdding(false); setPicked(null); }, onDone: () => { setAdding(false); load(); } })}</div>}
      {bulk && <BulkBar cfg={cfg} records={list} picked={checked} setPicked={setChecked} onDone={load} />}
      {bulk && pickable.length > 0 && (
        <label className="px-5 pt-2 flex items-center gap-2 text-xs text-nex-muted cursor-pointer">
          <input type="checkbox" checked={allOn} onChange={() => setChecked(allOn ? [] : pickable.map((r) => r.id))} data-testid={`${cfg.testid}-select-all`} className="accent-[#d4a017]" />
          Select all {pickable.length} shown
        </label>
      )}
      {list.length ? (
        <ul data-testid={`${cfg.testid}-list`} className="flex-1 overflow-y-auto divide-y divide-white/5">
          {list.map((r) => {
            const [tone, l] = cfg.chips[r.status];
            return (
              <li key={r.id} data-testid={`${cfg.testid}-row-${r.id}`} onClick={() => setSelected(r.id)} className="px-5 py-3 space-y-2 cursor-pointer hover:bg-nex-gold/5 transition-colors">
                <div className="flex flex-wrap items-center gap-3">
                  {bulk && <input type="checkbox" checked={checked.includes(r.id)} disabled={!pickable.includes(r)} onClick={(e) => e.stopPropagation()} onChange={() => tick(r.id)}
                    data-testid={`${cfg.testid}-check-${r.id}`} aria-label="Select" className="accent-[#d4a017] h-4 w-4 disabled:opacity-20" />}
                  {cfg.row(r)}
                  {cfg.residentLink !== false && <OpenResidentButton residentId={r.resident_id} name={r.resident_name} testid={`open-resident-${cfg.testid}-${r.id}`} />}
                  <Chip tone={tone} data-testid={`${cfg.testid}-status-${r.id}`}>{l}</Chip>
                  <span data-testid={`${cfg.testid}-amount-${r.id}`} className="ml-auto font-mono text-sm text-nex-gold">{cfg.amount(r)}</span>
                </div>
                <WorkflowActions cfg={cfg} rec={r} onChanged={load} />
              </li>
            );
          })}
        </ul>
      ) : <Empty testid={`${cfg.testid}-empty`}>No {cfg.plural}{status !== "all" ? ` with status ${status}` : ""} yet.</Empty>}
    </div>
  );
}
